import { lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import {
	selectCheckoutHost,
	selectShowModalCheckout,
	selectPurchaseTourCartId,
	selectPurchaseSuccessOrigin,
	selectShowPurchaseTour,
} from '../../redux/selectors/plans';
import { closePurchaseTour } from '../../redux/slices/plans.slice';

// Wraps the global `<CheckoutV2 />` so any surface that opens checkout without
// its own onboarding wrapper (sidebar, plans page, etc.) shows the success
// state inside the same surface instead of redirecting to
// `/successful-purchase/`. The legacy route is still served — it's the landing
// for 3DS / PayPal external redirects we can't intercept.
//
// The heavy checkout stack (CheckoutV2 + Stripe + success content) lives in
// GlobalCheckoutModalContent and only downloads when the modal actually opens
// (idle-prefetched after boot so opening stays instant).
const GlobalCheckoutModalContent = lazy(
	() => import('./GlobalCheckoutModalContent'),
);

// El tour vive FUERA del content a propósito: el content se desmonta en cuanto
// el checkout se cierra, y el tour tiene que aparecer justo después.
//
// Y vive AQUÍ y no en cada superficie que lo abre. Son cuatro orígenes —el
// checkout global, "Plan activo" de /plans, el mosaico de home y el hito del
// plan— y cada uno montaba su propio modal. Un solo montaje significa que
// arreglar el modal lo arregla en los cuatro.
const PurchaseTourModal = lazy(
	() => import('../../pages/SuccessfulPurchase/tour/PurchaseTourModal'),
);

const GlobalCheckoutModal = () => {
	const dispatch = useDispatch();
	const showModalCheckout = useSelector(selectShowModalCheckout);
	const checkoutHost = useSelector(selectCheckoutHost);
	const showPurchaseTour = useSelector(selectShowPurchaseTour);
	const purchaseTourCartId = useSelector(selectPurchaseTourCartId);
	const purchaseTourOrigin = useSelector(selectPurchaseSuccessOrigin);

	// Sin `cartId`: la compra recién hecha se lee de storage, así el recorrido
	// sigue contando como compra fresca y no como reentrada.
	if (showPurchaseTour) {
		return (
			<Suspense fallback={null}>
				<PurchaseTourModal
					open
					cartId={purchaseTourCartId ?? undefined}
					origin={purchaseTourOrigin}
					onClose={() => dispatch(closePurchaseTour())}
				/>
			</Suspense>
		);
	}

	// Yield to the onboarding checkout surfaces when they own the flow — both
	// gate on `showModalCheckout`, so without this the global modal and an
	// onboarding drawer would stack into a double checkout.
	if (!showModalCheckout || checkoutHost !== 'global') return null;

	return (
		<Suspense fallback={null}>
			<GlobalCheckoutModalContent />
		</Suspense>
	);
};

export default GlobalCheckoutModal;
